import { observable, computed, action, reaction } from 'mobx';
import { app } from '../App';
import { search } from './AllStores';
import { IMakeModelYearSuggestion, IMakeModelYearSuggestionsApi } from '../../../common/interfaces/search';
import { debounce as _debounce } from 'lodash';
import * as moment from 'moment';

export class Autocomplete {
    constructor() {
        this.fetchSuggestionsDebounced = _debounce(this.fetchSuggestionsDebounced, 300);
    }

    public suggestionsListHeightAnimationDuration: number = 300;

    private limit: number = 10;
    @observable private offset: number = 0;

    @observable public show: boolean = false;

    @computed public get showSuggestions(): boolean {
        return (this.show && this.suggestionsLoadedCount > 1) || this.suggestionsLoading;
    }

    @observable public suggestionsLoadingCount: number = 0;
    @computed public get suggestionsLoading(): boolean {
        return this.suggestionsLoadingCount > 0;
    }

    @observable public suggestionsFromDateTime: moment.Moment = moment();
    @observable public suggestionsFromQuery: string = '';
    @observable public suggestions: IMakeModelYearSuggestion[] = [];
    @observable public suggestionOnFocusIndex: number | null = null;

    @observable public suggestionsTotalCount: number = 0;

    @computed public get suggestionsLoadedCount(): number {
        return this.suggestions.length;
    }

    @computed public get suggestionsMoreExist(): boolean {
        return this.suggestionsTotalCount > this.suggestionsLoadedCount;
    }

    @computed public get suggestionOnFocus(): IMakeModelYearSuggestion | null {
        return this.suggestionOnFocusIndex !== null && this.suggestionOnFocusIndex < this.suggestionsLoadedCount
            ? this.suggestions[this.suggestionOnFocusIndex]
            : null;
    }

    @computed public get suggestionMoreOnFocus(): boolean {
        return this.suggestionOnFocusIndex !== null && this.suggestionOnFocusIndex >= this.suggestionsLoadedCount
            ? true : false;
    }

    @action public clearSuggestions = (withAnimation: boolean = true) => {
        this.show = false;

        if (withAnimation) {
            setTimeout(() => {
                this.clearSuggestionsReset();
            }, this.suggestionsListHeightAnimationDuration);
        } else {
            this.clearSuggestionsReset();
        }
    }

    @action private clearSuggestionsReset = () => {
        this.offset = 0;
        this.suggestions = [];
        this.suggestionsFromQuery = '';
        this.suggestionsLoadingCount = 0;
        this.suggestionOnFocusIndex = null;
    }

    public fetchSuggestionsDebounced = (append: boolean = false) => {
        this.fetchSuggestions(append);
    }

    @action public fetchSuggestions = async (append: boolean = false): Promise<boolean> => {
        try {
            this.show = true;
            this.suggestionsLoadingCount++;

            const query = search.searchInputValues.makeModelYear.trim();

            if (append) {
                this.offset = this.suggestionsLoadedCount;
            } else {
                this.offset = 0;
                this.suggestions = [];
            }

            if ((!append && query !== this.suggestionsFromQuery) || (append)) {
                const now = moment();
                const suggestionsService = await app.service('item-search-suggestion');
                const suggestions = await suggestionsService.find({
                    query: {
                        search: query,
                        limit: this.limit,
                        skip: this.offset
                    }
                }) as IMakeModelYearSuggestionsApi;

                if (now.isAfter(this.suggestionsFromDateTime)) {
                    this.suggestionsFromQuery = query;
                    this.suggestionsFromDateTime = now;
                    this.suggestionsTotalCount = suggestions.total;
                    this.suggestions = append ? this.suggestions.concat(suggestions.data) : suggestions.data;
                }
            }

            return true;
        } catch (err) {
            console.error(err);
            return err;
        } finally {
            this.suggestionsLoadingCount--;
        }
    }

    @action public turnSuggestionIntoSearchValue = (suggestion: IMakeModelYearSuggestion) => {
        let searchValue: string = suggestion.make + ' ' + suggestion.model;

        const currentSearchValueWords = search.searchInputValues.makeModelYear.split(' ');
        let searchValueDate: string = ' ';

        // Check if there are any 4 letter words that might be a date
        currentSearchValueWords.some((word) => {
            if (word.trim().length === 4) {
                const parsed = parseInt(word, 10);
                if (!isNaN(parsed)) {
                    searchValueDate += parsed;
                    return true;
                }
            }
        });

        // Check if there are any 2 letter words that might be a date
        if (searchValueDate.length <= 1) {
            currentSearchValueWords.some((word) => {
                if (word.trim().length === 2) {
                    const parsed = parseInt(word, 10);
                    if (!isNaN(parsed)) {
                        searchValueDate += parsed;
                        return true;
                    }
                }
            });
        }

        search.searchInputValues.makeModelYear = searchValue + searchValueDate;
        search.searchInputValues.modified = true;

        this.clearSuggestions();
    }

    @action public focusOnNextOrPreviousSuggestion = (next: boolean = true) => {
        if (this.suggestionsTotalCount > 0) {
            if (next) {
                this.suggestionOnFocusIndex = this.suggestionOnFocusIndex === null
                    || (this.suggestionsMoreExist && this.suggestionOnFocusIndex >= this.suggestionsLoadedCount)
                    || (!this.suggestionsMoreExist && this.suggestionOnFocusIndex >= this.suggestionsLoadedCount + 1)
                        ? 0 : this.suggestionOnFocusIndex + 1;
            } else {
                this.suggestionOnFocusIndex = this.suggestionOnFocusIndex > 0
                    ? this.suggestionOnFocusIndex - 1
                    : this.suggestionsLoadedCount;
            }
        } else {
            this.suggestionOnFocusIndex = null;
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/Autocomplete.ts